// src/components/card/SinglePanel.tsx

import {
  AccountBalanceTwoTone,
  FormatListBulletedTwoTone,
  InfoTwoTone,
  MemoryTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import Chart from 'react-google-charts'

import { DashboardId, PanelId, PanelPool } from '@/types/domain'
import { ConsumptionPool } from '@/types/domain/consumption'
import { FolderId, FolderName, FolderPool } from '@/types/domain/folder'
import { SensorId, SensorName, SensorPool } from '@/types/domain/sensor'
import { KARMA_Granularity } from '@/types/karma/granularity'

import {
  DlgPanelSensors,
  DlgPanelPresentationLevel,
  DlgPanelSummary,
  HStack,
  PanelDisplayNames,
  StackSelector,
  ChartTypeSelector,
  DlgDelete,
} from '..'
import {
  CORRECTION_ABSOLUT,
  CORRECTION_COOLING,
  CORRECTION_HEATING,
  correctionTypeData,
  colorSchemeConsumption,
  DDMMYY,
  DDMMYYYY,
  DDMMYYYYHHMM,
  germanDecimals,
  getHoursArray,
  getDaysArray,
  getMonthsArray,
  getYearsArray,
  MMMYY,
  userLocales,
  MMMMYYYY,
  WDDDMMYYYY,
  WDDDMMYYHHMM,
  YYYY,
} from '../../api'
import {
  useAppConfig,
  useConsumptionApi,
  useDatapointApi,
  useFolderPool,
  usePanelApi,
  useSensorPool,
} from '../../hooks'
import { BtnEditPanel } from '../button/BtnEditPanel'
import { usePreloader } from '../context/preloader'
import { DlgPanelFolders } from '../dialog/DlgPanelFolders'

const chartTypes = {
  line_chart: 0,
  pie_chart: 1,
  column_chart: 2,
  table: 3,
  area_chart: 4,
}

const indicatorType = {
  consumption: 1,
  consumption_per_sqm: 2,
  cost: 3,
  cost_per_sqm: 4,
  co2: 5,
  co2_per_sqm: 6,
  data_sampling_date: 7,

  heating_corrected_consumptions: 8,
  cooling_corrected_consumptions: 9,
}

const indicatorAttributes = {
  [indicatorType.consumption]: 'total_consumption',
  [indicatorType.consumption_per_sqm]: 'total_consumption_per_sqm',
  [indicatorType.cost]: 'total_cost',
  [indicatorType.cost_per_sqm]: 'total_cost_per_sqm',
  [indicatorType.co2]: 'total_co2',
  [indicatorType.co2_per_sqm]: 'total_co2_per_sqm',
}

const warningText =
  'Für bestimmte Messpunkte stehen im gewählten Zeitraum keine vollständigen Daten zur Verfügung. Mögliche Gründe: Die Messung begann erst zu einem späteren Zeitpunkt oder wurde unterbrochen!'

const indicatorWarnings: Record<number, string> = {
  [indicatorType.consumption]: warningText,
  [indicatorType.consumption_per_sqm]: warningText,
  [indicatorType.cost]: warningText,
  [indicatorType.cost_per_sqm]: warningText,
  [indicatorType.co2]: warningText,
  [indicatorType.co2_per_sqm]: warningText,
  [indicatorType.heating_corrected_consumptions]:
    'Die TB steht nicht für alle ausgewählten Messpunkte zur Verfügung!',
  [indicatorType.data_sampling_date]: warningText,
}

const digitGrouping = (digit, typeOfCorrection) =>
  (typeOfCorrection === CORRECTION_HEATING && Number(digit) === 0) ||
  (typeOfCorrection === CORRECTION_COOLING && Number(digit) === 0)
    ? '-'
    : Number(digit).toLocaleString(userLocales, {
        ...germanDecimals,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

const granularityTitle = (g: KARMA_Granularity) =>
  g === 'h'
    ? 'Stündlich'
    : g === 'd'
      ? 'Täglich'
      : g === 'm'
        ? 'Monatlich'
        : g === 'y'
          ? 'Jährlich'
          : g

const calculateAmount = (data, indicator, typeOfCorrection) => {
  let selectedData: any = {}

  if (typeOfCorrection === CORRECTION_ABSOLUT)
    selectedData = data?.attributes?.consumptions
  else if (typeOfCorrection === CORRECTION_COOLING)
    selectedData = data?.attributes?.cooling_corrected_consumptions
  else selectedData = data?.attributes?.heating_corrected_consumptions

  const {
    total_consumption,
    total_consumption_per_sqm,
    total_co2,
    total_co2_per_sqm,
    total_cost,
    total_cost_per_sqm,
  } = selectedData || {}
  return indicator === indicatorType.cost
    ? total_cost
    : indicator === indicatorType.cost_per_sqm
      ? total_cost_per_sqm
      : indicator === indicatorType.co2
        ? total_co2
        : indicator === indicatorType.co2_per_sqm
          ? total_co2_per_sqm
          : indicator === indicatorType.consumption
            ? total_consumption
            : indicator === indicatorType.consumption_per_sqm
              ? total_consumption_per_sqm
              : 0
}

const calculateRoundedPercentages = (values: number[]) => {
  const total = values.reduce((a, b) => a + b, 0)

  if (total === 0) return values.map(() => 0)

  const raw = values.map(v => (v / total) * 100)
  const floors = raw.map(v => Math.floor(v))

  const remainders = raw.map((v, i) => ({
    index: i,
    remainder: v - floors[i],
  }))

  const missing = 100 - floors.reduce((a, b) => a + b, 0)

  remainders
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, missing)
    .forEach(r => {
      floors[r.index]++
    })

  return floors
}

// color is derived from the name (alphabetical), not from the sort position,
// so identical selections get identical colors across all tiles
const createColorMap = (names: string[]): Record<string, string> =>
  Object.fromEntries(
    [...new Set(names)]
      .sort((a, b) => a.localeCompare(b, 'de'))
      .map((name, i) => [
        name,
        colorSchemeConsumption[i % colorSchemeConsumption.length],
      ])
  )

const getSensorLabel = (id: SensorId, sPool: SensorPool, fPool: FolderPool) => {
  const sensor = sPool.find(s => s.attributes.entity_id === id)
  const fName = fPool.find(f =>
    f.attributes.folder_sensors.some(s => Number(s.sensor_id) === id)
  )?.attributes.name

  return `${sensor?.attributes.name} | Gebäude ${fName || 'Unbekannt'}`
}

const createConsumptionValuesMap = (values, locales, format) =>
  new Map(
    values.map(item => [
      new Date(item.timepoint).toLocaleString(locales, format),
      item,
    ])
  )

const formatDateToLocaleString = (date, locales, format) =>
  typeof date === 'string'
    ? date
    : new Date(date).toLocaleString(locales, format)

const createBaseDataColumnChart = (
  consumptionData,
  granularity,
  fromDate,
  toDate,
  indicator,
  typeOfCorrection
) => {
  let columns = [granularityTitle(granularity)]

  const format =
    granularity === 'h'
      ? DDMMYYYYHHMM
      : granularity === 'd'
        ? DDMMYY
        : granularity === 'm'
          ? MMMYY
          : granularity === 'y'
            ? YYYY
            : DDMMYY

  let data =
    granularity === 'h'
      ? getHoursArray(new Date(fromDate), new Date(toDate))
      : granularity === 'd'
        ? getDaysArray(new Date(fromDate), new Date(toDate))
        : granularity === 'm'
          ? getMonthsArray(fromDate, toDate)
          : granularity === 'y'
            ? getYearsArray(fromDate, toDate)
            : []

  if (granularity === 'd')
    data = data.map(day => [day instanceof Date ? day : new Date(day)])

  if (granularity === 'm')
    data = data.map(day => [day instanceof Date ? day : new Date(day)])

  consumptionData.forEach(s => {
    let { consumptions } = s.attributes

    const {
      cooling_corrected_consumptions,
      heating_corrected_consumptions,
      name,
    } = s.attributes

    if (typeOfCorrection === CORRECTION_COOLING)
      consumptions = cooling_corrected_consumptions
    else if (typeOfCorrection === CORRECTION_HEATING)
      consumptions = heating_corrected_consumptions

    const valuesMap = createConsumptionValuesMap(
      consumptions?.values || [],
      userLocales,
      format
    )

    columns = [...columns, name]

    data = data.map(record => {
      const timeKey = formatDateToLocaleString(record[0], userLocales, format)
      const point: any = valuesMap.get(timeKey)
      const aggregated = point
        ? indicator === indicatorType.cost
          ? (point.cost ?? 0)
          : indicator === indicatorType.cost_per_sqm
            ? (point.cost_per_sqm ?? 0)
            : indicator === indicatorType.co2
              ? (point.co2 ?? 0)
              : indicator === indicatorType.co2_per_sqm
                ? (point.co2_per_sqm ?? 0)
                : indicator === indicatorType.consumption
                  ? (point.value ?? 0)
                  : indicator === indicatorType.consumption_per_sqm
                    ? (point.value_per_sqm ?? 0)
                    : 0
        : 0
      const val = Number.isFinite(aggregated)
        ? Number(aggregated.toFixed(2))
        : 0
      return [...record, val || 0]
    })
  })

  data.sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())

  if (data.length === 0) data = [granularity, granularity]

  data = [columns, ...data]

  if (data.length === 1) data.push(columns.map(() => 0))

  return data
}

const createBaseDataFolderColumnChart = (
  granularity,
  consumptions,
  fromDate,
  toDate,
  indicator,
  typeOfCorrection
) => {
  let columns = [granularityTitle(granularity)]

  const format =
    granularity === 'h'
      ? DDMMYYYYHHMM
      : granularity === 'd'
        ? DDMMYY
        : granularity === 'm'
          ? MMMYY
          : granularity === 'y'
            ? YYYY
            : DDMMYY

  let data =
    granularity === 'h'
      ? getHoursArray(new Date(fromDate), new Date(toDate))
      : granularity === 'd'
        ? getDaysArray(new Date(fromDate), new Date(toDate))
        : granularity === 'm'
          ? getMonthsArray(fromDate, toDate)
          : granularity === 'y'
            ? getYearsArray(fromDate, toDate)
            : []

  if (granularity === 'd')
    data = data.map(day => [day instanceof Date ? day : new Date(day)])

  if (granularity === 'm')
    data = data.map(day => [day instanceof Date ? day : new Date(day)])

  consumptions.forEach(s => {
    let { consumptions } = s.attributes

    const {
      heating_corrected_consumptions,
      cooling_corrected_consumptions,
      name,
    } = s.attributes

    if (typeOfCorrection === CORRECTION_COOLING)
      consumptions = cooling_corrected_consumptions
    else if (typeOfCorrection === CORRECTION_HEATING)
      consumptions = heating_corrected_consumptions

    const valuesMap = createConsumptionValuesMap(
      consumptions?.values || [],
      userLocales,
      format
    )

    columns = [...columns, name]

    data = data.map(record => {
      const timeKey = formatDateToLocaleString(record[0], userLocales, format)
      const point: any = valuesMap.get(timeKey)
      let aggregated = 0
      if (point) {
        aggregated =
          indicator === indicatorType.cost
            ? (point.cost ?? 0)
            : indicator === indicatorType.cost_per_sqm
              ? (point.cost_per_sqm ?? 0)
              : indicator === indicatorType.co2
                ? (point.co2 ?? 0)
                : indicator === indicatorType.co2_per_sqm
                  ? (point.co2_per_sqm ?? 0)
                  : indicator === indicatorType.consumption
                    ? (point.value ?? 0)
                    : indicator === indicatorType.consumption_per_sqm
                      ? (point.value_per_sqm ?? 0)
                      : 0
      }
      let val = Number.isFinite(aggregated) ? +aggregated.toFixed(2) : 0
      if (indicator === 2 || indicator === 4 || indicator === 6)
        val = val / consumptions.length
      return [...record, val || 0]
    })
  })

  data.sort((a, b) => +new Date(a[0]) - +new Date(b[0]))

  if (data.length === 0) data = [granularity, granularity]

  data = [columns, ...data]

  if (data.length === 1) data.push(columns.map(() => 0))

  return data
}

const addTooltipColumnChart = (data, granularity, unit) => {
  const combined = []
  const dateFormat =
    granularity === 'h'
      ? WDDDMMYYHHMM
      : granularity === 'd'
        ? WDDDMMYYYY
        : granularity === 'm'
          ? MMMMYYYY
          : granularity === 'y'
            ? YYYY
            : DDMMYYYY

  data.forEach((row, key) => {
    if (!Array.isArray(row)) return

    if (key === 0) {
      combined.push([row[key]])

      row.forEach((col, j) => {
        if (j > 0) {
          combined[key].push(col, {
            type: 'string',
            role: 'tooltip',
            p: { html: true },
          })
        }
      })
    } else {
      combined[key] = [row[0]]

      row.forEach((col, j) => {
        if (j > 0) {
          const name = data[0][j]
          const timestamp =
            row[0] instanceof Date
              ? row[0].toLocaleString(userLocales, dateFormat)
              : String(row[0])

          combined[key].push(
            col,
            `<div style="padding:5px 10px; min-width:220px;">
             <p><strong>${timestamp}</strong></p>
             <p>${name}: <strong>${digitGrouping(col, null)} ${unit}</strong></p>
           </div>`
          )
        }
      })
    }
  })

  return combined
}

type SinglePanelProps = {
  dashboardId: DashboardId
  id: PanelId
  onDeleted?: (_panelId?: PanelId) => void
  panelsPerRow?: number
  refreshKey?: any
  removePanel?: (_dashboardId: DashboardId, _panelId: PanelId) => Promise<void>
}

type PanelTile = {
  name: string
  position: number
  indicator: number
  graphic_type: number
  granularity: KARMA_Granularity
  presentationLevel: number
  typeOfCorrection?: number
  start_date: string
  end_date: string
  unit?: string
  isStacked?: boolean
  chartLegend?: boolean
}

export const SinglePanel = ({
  dashboardId: dId,
  id,
  onDeleted,
  panelsPerRow,
  refreshKey,
}: SinglePanelProps) => {
  const { appConfig } = useAppConfig()
  const { getPanels, update: updatePanel, remove: removePanel } = usePanelApi()
  const { getDatapointsByPanelIds, update: updateDatapoints } =
    useDatapointApi()
  const { getDashboardConsumption } = useConsumptionApi()

  const theme = useTheme()

  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()

  const [panelsData, setPanelsData] = useState<PanelPool>([])
  const [consumptionData, setConsumptionData] = useState<ConsumptionPool>([])

  const [dataPoints, setDataPoints] = useState<any>({})

  const [showDlg, setShowDlg] = useState<
    false | 'Edit' | 'Delete' | 'Sensors' | 'PresentationLevel' | 'Summary'
  >(false)

  const [extractedSIds, setExtractedSensorIDs] = useState<SensorId[]>([])
  const [extractedSNames, setExtractedSensorNames] = useState<SensorName[]>([])
  const [extractedFIds, setExtractedFIds] = useState<FolderId[]>([])
  const [extractedFNames, setExtractedFNames] = useState<FolderName[]>([])

  const LVL_FOLDER: number = 1
  const LVL_SENSOR: number = 2

  const [tile, setTile] = useState<PanelTile>({
    name: '',
    position: 0,
    indicator: indicatorType.consumption,
    graphic_type: chartTypes.column_chart,
    granularity: 'd',
    presentationLevel: LVL_SENSOR,
    typeOfCorrection: CORRECTION_ABSOLUT,
    start_date: '',
    end_date: '',
    unit: 'kWh',
    isStacked: true,
    chartLegend: true,
  })

  const [showWarning, setShowWarning] = useState<boolean>(false)
  const [warningText, setWarningText] = useState<string>('')
  const [warningSensors, setWarningSensors] = useState<ReactNode[]>([])
  const [warningAnchorEl, setWarningAnchorEl] = useState<HTMLElement | null>(
    null
  )

  const [total, setTotal] = useState<number>(0)
  const [chartData, setChartData] = useState<any[]>([])
  const [chartColors, setChartColors] = useState<string[]>([])
  const [colorMap, setColorMap] = useState<Record<string, string>>({})
  const [noPieChartData, setNoPieChartData] = useState<boolean>(false)

  const [availableCorrectionTypes, setAvailableCorrectionTypes] = useState<
    any[]
  >([])

  const [selectedFolders, setSelectedFolders] = useState<FolderId[]>([])
  const [selectedSensors, setSelectedSensors] =
    useState<SensorId[]>(extractedSIds)

  const chartType =
    tile.graphic_type === chartTypes.pie_chart
      ? 'PieChart'
      : tile.graphic_type === chartTypes.column_chart
        ? 'ColumnChart'
        : tile.graphic_type === chartTypes.table
          ? 'Table'
          : tile.graphic_type === chartTypes.area_chart
            ? 'AreaChart'
            : 'LineChart'

  const chartIndicator =
    tile.indicator === indicatorType.consumption
      ? 'Verbrauch'
      : tile.indicator === indicatorType.consumption_per_sqm
        ? 'Verbrauch/m²'
        : tile.indicator === indicatorType.cost
          ? 'Kosten'
          : tile.indicator === indicatorType.cost_per_sqm
            ? 'Kosten/m²'
            : tile.indicator === indicatorType.co2
              ? 'CO₂-Ausstoß'
              : 'CO₂-Ausstoß/m²'

  const chartUnit =
    tile.indicator === indicatorType.consumption ||
    tile.indicator === indicatorType.consumption_per_sqm
      ? tile.unit
      : tile.indicator === indicatorType.cost ||
          tile.indicator === indicatorType.cost_per_sqm
        ? '€'
        : tile.indicator === indicatorType.co2 ||
            tile.indicator === indicatorType.co2_per_sqm
          ? 'kg'
          : 'kWh'

  const listSource = [
    ...new Set(
      tile.presentationLevel === LVL_SENSOR ? extractedSNames : extractedFNames
    ),
  ].sort((a, b) => a.localeCompare(b, 'de'))

  const listLabel = tile.presentationLevel === LVL_SENSOR ? 'sensor' : 'folder'

  const deletePanel = async () => {
    await removePanel(dId, id)
    setShowDlg(false)
    if (typeof onDeleted === 'function') await onDeleted(id)
  }

  const handleStackMode = (event, isStacked) => {
    if (isStacked !== null) setTile(prev => ({ ...prev, isStacked }))
  }

  const handleGraphicType = (event, graphicType) => {
    if (graphicType !== null) {
      const newType =
        graphicType === 'ColumnChart'
          ? chartTypes.column_chart
          : graphicType === 'LineChart'
            ? chartTypes.line_chart
            : graphicType === 'AreaChart'
              ? chartTypes.area_chart
              : chartTypes.pie_chart
      setTile(prev => ({ ...prev, graphic_type: newType }))
    }
  }

  const handleTypeOfCorrection = typeOfCorrection => {
    setTile(prev => ({ ...prev, typeOfCorrection }))
    setWarningSensors([])
  }

  const onCloseSettings = () => {
    setShowDlg(false)
    setSelectedSensors(extractedSIds)
    setSelectedFolders([...new Set(extractedFIds)])
  }

  const onToggleLegend = () =>
    setTile(prev => ({ ...prev, chartLegend: !prev.chartLegend }))

  const onToggleWarning = event =>
    setWarningAnchorEl(warningAnchorEl ? null : event.currentTarget)

  const onUpdatePanel = () => {
    const editPanel = {
      entity_id: id,
      dashboard_id: dId,
      name: tile.name,
      position: tile.position,
      indicator: tile.indicator,
      graphic_type: tile.graphic_type,
      start_date: new Date(tile.start_date).toISOString().split('T')[0],
      end_date: new Date(tile.end_date).toISOString().split('T')[0],
      presentation_level: tile.presentationLevel,
      granularity: tile.granularity,
      type_of_correction: tile.typeOfCorrection,
      sensor_ids: selectedSensors,
    }

    const editDataPoint = { panel_id: id, sensor_ids: selectedSensors }

    updatePanel(editPanel)
    updateDatapoints(dId, editDataPoint)
  }

  useEffect(() => {
    setChartData([])
    setNoPieChartData(false)
  }, [tile.graphic_type])

  useEffect(() => {
    const fetchPanels = async () => {
      const data = await getPanels(dId)
      setPanelsData(data)
    }
    fetchPanels()
  }, [getPanels, dId, refreshKey])

  useEffect(() => {
    if (panelsData?.length === 0) return

    const handle = panelsData.find(f => Number(f.id) === id)
    if (!handle) return

    const {
      end_date,
      granularity,
      graphic_type: graphicType,
      indicator,
      name,
      position,
      presentation_level: presentationLevel,
      type_of_correction: typeOfCorrection = 3,
      start_date,
    } = handle.attributes

    setTile({
      chartLegend: true,
      end_date,
      graphic_type: Number(graphicType),
      granularity: granularity as KARMA_Granularity,
      indicator: Number(indicator),
      isStacked: true,
      presentationLevel: Number(presentationLevel),
      typeOfCorrection: Number(typeOfCorrection),
      name,
      position,
      start_date,
      unit: 'kWh',
    })
  }, [panelsData, id, refreshKey])

  useEffect(() => {
    const fetchConsumption = async () => {
      const data = await getDashboardConsumption(dId)

      setConsumptionData(data)
    }

    fetchConsumption()
  }, [getDashboardConsumption, dId, refreshKey])

  useEffect(() => {
    const fetchDataPoints = async () => {
      const data = await getDatapointsByPanelIds(dId, panelsData)

      setDataPoints(data)
    }

    fetchDataPoints()
  }, [getDatapointsByPanelIds, dId, panelsData, refreshKey])

  useEffect(() => {
    if (
      consumptionData.length === 0 ||
      Object.keys(dataPoints.items || {}).length === 0
    )
      return

    const sensorIds = dataPoints.items[id] ?? []
    const cData = consumptionData.filter(f => sensorIds.includes(Number(f.id)))

    setExtractedSensorIDs(cData.map(item => Number(item.id)))
  }, [consumptionData, dataPoints, id])

  useEffect(() => {
    const fIds = new Set()
    const fNames = new Set()
    const sensorNames = new Set()
    let unit = null

    const uniqueSIds = [...new Set(extractedSIds)]

    uniqueSIds.forEach((id: SensorId) => {
      const fRecord = fPool.find(f =>
        f.attributes.folder_sensors.some(s => Number(s.sensor_id) === id)
      )

      const fName: FolderName = fRecord ? fRecord.attributes.name : ''

      if (fRecord) {
        fIds.add(Number(fRecord.id))
        fNames.add(fName)
      }

      const sensor = sPool.find(f => f.attributes.entity_id === id)
      if (sensor) {
        const { name, unit: sensorUnit } = sensor.attributes
        const fullName = `${name} | Gebäude ${fName || 'Unbekannt'}`
        sensorNames.add(fullName)

        if (!unit) unit = sensorUnit
      }
    })

    setExtractedFIds(Array.from(fIds) as FolderId[])
    setExtractedFNames(Array.from(fNames) as FolderName[])
    setExtractedSensorNames(Array.from(sensorNames) as string[])
    if (unit) setTile(prev => ({ ...prev, unit }))
  }, [extractedSIds, fPool, sPool])

  useEffect(
    () => setSelectedFolders([...new Set(extractedFIds)]),
    [extractedFIds]
  )

  useEffect(
    () => setSelectedSensors([...new Set(extractedSIds)]),
    [extractedSIds]
  )

  useEffect(() => {
    if (!consumptionData) return

    const cData = consumptionData.filter(f =>
      extractedSIds.includes(Number(f.id))
    )

    const getSensorById = (id: SensorId) => sPool.find(s => Number(s.id) === id)

    const createTooltip = (label: string, amount: number) => `
    <div style="padding:5px 10px; min-width:220px;">
      <p>${label}: <strong>${digitGrouping(amount, tile.typeOfCorrection)} ${chartUnit}</strong></p>
    </div>
  `

    if (tile.presentationLevel === LVL_SENSOR) {
      const isTimeSeriesChart = [
        'ColumnChart',
        'LineChart',
        'AreaChart',
      ].includes(chartType)
      const isAverageIndicator = [2, 4, 6].includes(tile.indicator)

      const getTotal = () => {
        let total = 0
        let numberOfSensors = 0

        cData.forEach(item => {
          if (!item) return

          total += calculateAmount(item, tile.indicator, tile.typeOfCorrection)
          numberOfSensors++
        })

        return isAverageIndicator && numberOfSensors > 0
          ? total / numberOfSensors
          : total
      }

      const getSensorRows = () =>
        cData
          .map(item => {
            if (!item) return null

            const sensor = getSensorById(Number(item.id))
            if (!sensor) return null

            const { name, entity_id } = sensor.attributes

            const amount = calculateAmount(
              item,
              tile.indicator,
              tile.typeOfCorrection
            )

            const folder =
              fPool.find(f =>
                f.attributes.folder_sensors.some(s => s.sensor_id === entity_id)
              )?.attributes.name ?? ''

            return { id: Number(item.id), name, amount, folder }
          })
          .filter(Boolean)

      const sensorColorMap = createColorMap(
        extractedSIds.map(sId => getSensorLabel(sId, sPool, fPool))
      )
      const colorOf = (sId: SensorId) =>
        sensorColorMap[getSensorLabel(sId, sPool, fPool)]

      const buildTimeSeriesData = () =>
        addTooltipColumnChart(
          createBaseDataColumnChart(
            cData,
            tile.granularity,
            tile.start_date,
            tile.end_date,
            tile.indicator,
            tile.typeOfCorrection
          ),
          tile.granularity,
          chartUnit
        )

      const buildPieSensorData = () => {
        const rows = getSensorRows().map(row => [
          row.name,
          row.amount,
          createTooltip(row.name, row.amount),
        ])

        const values = rows.map(row => Number(row?.[1]) || 0)
        const percentages = calculateRoundedPercentages(values)

        return [
          [
            'Messpunkt',
            'Verbrauch',
            { role: 'tooltip', type: 'string', p: { html: true } },
          ],
          ...rows.map((row, index) => [
            `${percentages[index]}%`,
            Number(row?.[1]) || 0,
            row?.[2],
          ]),
        ]
      }

      const addColors = rows =>
        rows.map(row => ({ ...row, color: colorOf(row.id) }))

      const buildBarCell = (
        title: string,
        amount: number,
        maxValue: number,
        color: string
      ) => {
        const barWidth = (amount / maxValue) * 100

        return `
      <div style="display:flex; align-items:center; gap:4px;">
        <h3>${title}</h3>
      </div>
      <div class="bar" style="background-color:${color}; width:${barWidth}%"></div>
    `
      }

      const buildTableSensorData = () => {
        const rows = getSensorRows().sort((a, b) => b.amount - a.amount)
        const maxValue = Math.max(...rows.map(r => r.amount), 1)

        const data = addColors(rows).map(row => [
          {
            v: row.name,
            f: buildBarCell(
              `${row.name} | ${row.folder}`,
              row.amount,
              maxValue,
              row.color
            ),
          },
          {
            v: row.amount,
            f: `${digitGrouping(row.amount, tile.typeOfCorrection)} ${chartUnit}`,
          },
        ])

        return [['Messpunkt', chartIndicator], ...data]
      }

      let data
      let colors: string[] = []

      if (isTimeSeriesChart) {
        data = buildTimeSeriesData()
        colors = cData.map(item => colorOf(Number(item.id)))
      } else if (chartType === 'PieChart') {
        data = buildPieSensorData()
        colors = getSensorRows().map(row => colorOf(row.id))
      } else {
        data = buildTableSensorData()
      }

      const total = getTotal()

      setNoPieChartData(total === 0)
      setChartData(data)
      setChartColors(colors)
      setColorMap(sensorColorMap)
      setTotal(total)
    } else if (tile.presentationLevel === LVL_FOLDER) {
      const isTimeSeriesChart = [
        'ColumnChart',
        'LineChart',
        'AreaChart',
      ].includes(chartType)
      const isAverageIndicator = [2, 4, 6].includes(tile.indicator)

      let data: any[] = []
      let totalObject = 0
      let numberOfFolders = 0

      const getFolderConsumptions = (folder: any) => {
        const targetSensorIds = folder.attributes.folder_sensors
          .map(({ sensor_id }) => String(sensor_id))
          .filter(id => extractedSIds.map(String).includes(id))

        return consumptionData.filter(item =>
          targetSensorIds.includes(String(item.id))
        )
      }

      const calculateFolderTotal = (items: any[]) => {
        const total = items.reduce((sum, item) => {
          if (!item) return sum

          return (
            sum + calculateAmount(item, tile.indicator, tile.typeOfCorrection)
          )
        }, 0)

        return isAverageIndicator && items.length > 0
          ? total / items.length
          : total
      }

      const buildTimeSeriesFolderData = (name: string, items: any[]) => {
        const chartData = createBaseDataFolderColumnChart(
          tile.granularity,
          items,
          tile.start_date,
          tile.end_date,
          tile.indicator,
          tile.typeOfCorrection
        )

        chartData.forEach((row, index) => {
          if (index === 0) return

          const date = row[0]
          const sum = row
            .slice(1)
            .reduce((partialSum, value) => partialSum + value, 0)

          chartData[index] = [date, sum]
        })

        chartData[0] = ['Zeit', name]

        return chartData
      }

      const buildPieFolderRow = (name: string, items: any[]) => {
        const total = calculateFolderTotal(items)

        return [name, total, createTooltip(name, total)]
      }

      const buildTableFolderRow = (name: string, items: any[]) => {
        const total = calculateFolderTotal(items)

        return [
          {
            v: name,
            f: name,
          },
          {
            v: total,
            f: ` ${digitGrouping(total, tile.typeOfCorrection)} ${chartUnit}`,
          },
        ]
      }

      const combineTimeSeriesData = (chartDataSets: any[]) => {
        if (chartDataSets.length === 0) return []

        const combinedData = chartDataSets[0]

        chartDataSets.slice(1).forEach(objects => {
          objects.forEach((row, index) => {
            combinedData[index].push(row[1])
          })
        })

        return addTooltipColumnChart(combinedData, tile.granularity, chartUnit)
      }

      const addPieHeaderAndPercentages = (rows: any[]) => {
        const values = rows.map(row => Number(row?.[1]) || 0)
        const percentages = calculateRoundedPercentages(values)

        return [
          [
            'Gebäude',
            'Wert',
            { role: 'tooltip', type: 'string', p: { html: true } },
          ],
          ...rows.map((row, index) => [
            `${percentages[index]}%`,
            Number(row?.[1]) || 0,
            row?.[2],
          ]),
        ]
      }

      const addTableRanking = (rows: any[]) => {
        if (rows.length === 0) return rows

        rows.sort((a, b) => b[1].v - a[1].v)

        const maxValue = rows[0][1].v || 1

        rows.forEach(row => {
          const barWidth = (row[1].v / maxValue) * 100
          const color = folderColorMap[row[0].v]

          row[0].f = `
        <div style="display:flex; gap:4px;">
          <h3>${row[0].f}</h3>
        </div>
        <div class="bar" style="background-color:${color}; width:${barWidth}%;"></div>
      `
        })

        return [['Gebäude ', chartIndicator], ...rows]
      }

      const folderColorMap = createColorMap(
        selectedFolders
          .map(fId => fPool.find(f => Number(f.id) === fId)?.attributes.name)
          .filter(Boolean)
      )
      const seriesNames: string[] = []

      selectedFolders.forEach((id: FolderId) => {
        const folder = fPool.find(f => Number(f.id) === id)
        if (!folder) return

        const { name } = folder.attributes
        const folderConsumptions = getFolderConsumptions(folder)

        if (folderConsumptions.length === 0) return

        numberOfFolders++
        seriesNames.push(name)

        const folderTotal = calculateFolderTotal(folderConsumptions)
        totalObject += folderTotal

        if (isTimeSeriesChart) {
          data.push(buildTimeSeriesFolderData(name, folderConsumptions))
          return
        }

        if (chartType === 'PieChart') {
          data.push(buildPieFolderRow(name, folderConsumptions))

          return
        }

        data.push(buildTableFolderRow(name, folderConsumptions))
      })

      if (isTimeSeriesChart) data = combineTimeSeriesData(data)

      if (chartType === 'PieChart') data = addPieHeaderAndPercentages(data)

      if (chartType === 'Table') data = addTableRanking(data)

      setChartData(data)
      setChartColors(seriesNames.map(name => folderColorMap[name]))
      setColorMap(folderColorMap)
      setTotal(
        isAverageIndicator && numberOfFolders > 0
          ? totalObject / numberOfFolders
          : totalObject
      )
    }
  }, [
    chartIndicator,
    chartType,
    chartUnit,
    consumptionData,
    fPool,
    tile,
    tile.end_date,
    tile.granularity,
    tile.indicator,
    tile.presentationLevel,
    tile.start_date,
    tile.typeOfCorrection,
    sPool,
    selectedFolders,
    extractedSIds,
  ])

  useEffect(() => {
    if (selectedSensors.length === 0) return

    setShowWarning(false)
    setWarningText('')
    setWarningSensors([])

    const newWarnings = []
    const warningIds = []
    const warningTexts = []

    for (const sId of selectedSensors) {
      const sensor = sPool.find(f => Number(f.id) === sId)
      if (!sensor) continue

      const {
        entity_id,
        name: sensorName,
        data_sampling_date: dsd,
      } = sensor.attributes

      const sensorConsumption = consumptionData.find(f => Number(f.id) === sId)
      const {
        has_predecessor,
        has_successor,
        predecessor_id,
        successor_id,
        predecessor_data_sampling_date: phdsd,
        successor_data_sampling_date: shdsd,
        predecessor_has_consumptions: phc,
        predecessor_has_heating_corrected_consumptions: phhcc,
        predecessor_has_cooling_corrected_consumptions: phccc,
        successor_has_consumptions: shc,
        successor_has_heating_corrected_consumptions: shhcc,
        successor_has_cooling_corrected_consumptions: shccc,
      } = sensorConsumption?.attributes || {}

      const fRecord = fPool.find(f =>
        f.attributes.folder_sensors.some(s => s.sensor_id === sId)
      )
      const fName: FolderName = fRecord ? fRecord.attributes.name : ''
      const fullName = `${sensorName} | Gebäude ${fName || 'Unbekannt'}`

      const startDate = new Date(tile?.start_date).getTime()

      const dsdTime = dsd ? new Date(dsd).getTime() : null
      const predecessorDSDTime =
        phdsd instanceof Date ? new Date(phdsd).getTime() : null
      const successorDSDTime =
        shdsd instanceof Date ? new Date(shdsd).getTime() : null

      const coversStartDate = [
        dsdTime,
        predecessorDSDTime,
        successorDSDTime,
      ].some(time => time !== null && time <= startDate)

      const chainId = has_predecessor
        ? `chain-${predecessor_id || phdsd}`
        : has_successor
          ? `chain-${successor_id || shdsd}`
          : `chain-${entity_id}`

      const attributes = indicatorAttributes[tile.indicator]

      const hasNoCurrentConsumptions =
        sensorConsumption?.attributes.consumptions?.[attributes] === null
      const hasNoCurrentHeatingCorrectedConsumptions =
        sensorConsumption?.attributes?.heating_corrected_consumptions?.values
          ?.length === 0
      const hasNoCurrentCoolingCorrectedConsumptions =
        sensorConsumption?.attributes?.cooling_corrected_consumptions?.[
          attributes
        ] === null

      const predecessorExistsButNoData = has_predecessor && !phc
      const successorExistsButNoData = has_successor && !shc

      const predecessorExistsButNoHeatingCorrectedData =
        has_predecessor && !phhcc
      const predecessorExistsButNoCoolingCorrectedData =
        has_predecessor && !phccc
      const successorExistsButNoHeatingCorrectedData = has_successor && !shhcc
      const successorExistsButNoCoolingCorrectedData = has_successor && !shccc

      const warningChip = () => (
        <>
          {fullName}
          <br />
          <Chip
            label={
              <>
                Datenbeginn:{' '}
                {isNaN(dsdTime)
                  ? 'Kein Datum verfügbar'
                  : new Date(dsdTime).toLocaleString(userLocales, DDMMYYYY)}
              </>
            }
          />
        </>
      )

      if (
        !coversStartDate &&
        appConfig.showMissingSensorDataNotice &&
        !warningIds.includes(chainId)
      ) {
        warningTexts.push(indicatorWarnings[7])
        newWarnings.push(warningChip())
        warningIds.push(chainId)
      }

      if (
        coversStartDate &&
        appConfig.showMissingSensorDataNotice &&
        hasNoCurrentConsumptions &&
        (predecessorExistsButNoData || successorExistsButNoData) &&
        !warningIds.includes(chainId)
      ) {
        warningTexts.push(indicatorWarnings[tile.indicator])
        newWarnings.push(warningChip())
        warningIds.push(chainId)
      }

      if (
        tile.typeOfCorrection === CORRECTION_HEATING &&
        hasNoCurrentHeatingCorrectedConsumptions &&
        (predecessorExistsButNoHeatingCorrectedData ||
          successorExistsButNoHeatingCorrectedData) &&
        !warningIds.includes(chainId)
      ) {
        warningTexts.push(
          indicatorWarnings[indicatorType.heating_corrected_consumptions]
        )
        newWarnings.push(warningChip())
        warningIds.push(chainId)
      }

      if (
        tile.typeOfCorrection === CORRECTION_COOLING &&
        hasNoCurrentCoolingCorrectedConsumptions &&
        (predecessorExistsButNoCoolingCorrectedData ||
          successorExistsButNoCoolingCorrectedData) &&
        !warningIds.includes(chainId)
      ) {
        warningTexts.push(
          indicatorWarnings[indicatorType.cooling_corrected_consumptions]
        )
        newWarnings.push(warningChip())
        warningIds.push(chainId)
      }
    }

    if (newWarnings.length > 0) {
      setShowWarning(true)

      const priorityOrder = [
        indicatorWarnings[indicatorType.data_sampling_date],
        indicatorWarnings[tile.indicator],
        indicatorWarnings[indicatorType.heating_corrected_consumptions],
        indicatorWarnings[indicatorType.cooling_corrected_consumptions],
      ]

      const chosenText =
        priorityOrder.find(text => warningTexts.includes(text)) ||
        warningTexts[0]

      setWarningText(chosenText)
      setWarningSensors(newWarnings)
    }
  }, [
    appConfig.showMissingSensorDataNotice,
    selectedSensors,
    consumptionData,
    tile,
    tile.indicator,
    tile.typeOfCorrection,
    tile.presentationLevel,
    sPool,
    fPool,
  ])

  useEffect(() => {
    if (consumptionData.length === 0) return
    const filteredData = consumptionData.filter(s =>
      extractedSIds.includes(Number(s.id))
    )

    const hasHeating = filteredData.some(
      s => s.attributes?.heating_corrected_consumptions?.values?.length > 0
    )

    const hasCooling = filteredData.some(
      s => s.attributes?.cooling_corrected_consumptions?.values?.length > 0
    )

    let availableCorrectionTypes = []

    if (hasHeating && hasCooling) {
      availableCorrectionTypes = correctionTypeData
    } else if (hasHeating && !hasCooling) {
      availableCorrectionTypes = correctionTypeData.filter(
        ct => ct.value === CORRECTION_HEATING || ct.value === CORRECTION_ABSOLUT
      )
    } else if (!hasHeating && hasCooling) {
      availableCorrectionTypes = correctionTypeData.filter(
        ct => ct.value === CORRECTION_COOLING || ct.value === CORRECTION_ABSOLUT
      )
    } else {
      availableCorrectionTypes = availableCorrectionTypes =
        correctionTypeData.filter(ct => ct.value === CORRECTION_ABSOLUT)
    }

    if (availableCorrectionTypes.length > 0)
      setAvailableCorrectionTypes(availableCorrectionTypes)
  }, [consumptionData, extractedSIds])

  const panelHeight = 250

  const isColumn =
    chartType === 'ColumnChart' ||
    chartType === 'LineChart' ||
    chartType === 'AreaChart'

  const chartArea = isColumn
    ? { top: 20, left: 64, bottom: 48, right: 16 }
    : { top: 10, left: 10, bottom: 10, right: 10 }

  const { isLoading } = usePreloader()

  return (
    <Card
      sx={{
        minWidth: 275,
        position: 'relative',
        border: `1px solid ${
          typeof warningText === 'string' && warningText.length > 0
            ? theme.palette.error.main
            : tile.typeOfCorrection !== 3
              ? theme.palette.primary.main
              : '#ccc'
        }`,
        borderRadius: 5,
        m: 1,
      }}
    >
      <DlgPanelFolders
        data={{ folders: selectedFolders, panel: tile, mode: 'edit' }}
        onChange={panel => setTile(panel)}
        onClose={onCloseSettings}
        onGotoNext={() => setShowDlg('Sensors')}
        open={showDlg === 'Edit'}
        setSelectedFolders={setSelectedFolders}
        setSelectedSensors={setSelectedSensors}
      />

      <DlgPanelSensors
        data={{ folders: selectedFolders, sensors: selectedSensors }}
        onChange={sIds => setSelectedSensors(sIds)}
        onClose={onCloseSettings}
        onGotoPrev={() => setShowDlg('Edit')}
        onGotoNext={() => setShowDlg('PresentationLevel')}
        open={showDlg === 'Sensors'}
      />

      <DlgPanelPresentationLevel
        data={tile.presentationLevel}
        onChange={presentationLevel =>
          setTile(prev => ({ ...prev, presentationLevel }))
        }
        onClose={onCloseSettings}
        onGotoNext={() => setShowDlg('Summary')}
        onGotoPrev={() => setShowDlg('Sensors')}
        open={showDlg === 'PresentationLevel'}
      />

      <DlgPanelSummary
        data={{
          folders: selectedFolders,
          sensors: selectedSensors,
          panelId: id,
        }}
        onCancel={() => setShowDlg(false)}
        onConfirm={onUpdatePanel}
        onEditPanel={() => setShowDlg('Edit')}
        onDeletePanel={() => setShowDlg('Delete')}
        onGotoPrev={() => setShowDlg('PresentationLevel')}
        open={showDlg === 'Summary'}
      />

      <DlgDelete
        onCancel={() => setShowDlg(false)}
        onConfirm={deletePanel}
        open={showDlg === 'Delete'}
      >
        <Alert severity={'info'} sx={{ p: 2 }}>
          Möchten Sie Kachel <strong>{tile.name}</strong> wirklich löschen?
        </Alert>
      </DlgDelete>

      <CardHeader
        action={
          <HStack sx={{ width: 1 }}>
            {chartType === 'Table' ? null : (
              <Tooltip
                title={
                  tile.chartLegend === true
                    ? 'Legende ausblenden'
                    : 'Legende einblenden'
                }
              >
                <IconButton
                  disabled={panelsPerRow === 5}
                  onClick={onToggleLegend}
                >
                  <FormatListBulletedTwoTone />
                </IconButton>
              </Tooltip>
            )}
            <BtnEditPanel
              disabled={isLoading}
              onClick={() => setShowDlg('Summary')}
            />
          </HStack>
        }
        avatar={
          <Avatar>
            {tile.presentationLevel === LVL_SENSOR ? (
              <MemoryTwoTone />
            ) : (
              <AccountBalanceTwoTone />
            )}
          </Avatar>
        }
        slotProps={{ title: { variant: 'h6' } }}
        subheader={
          tile?.typeOfCorrection === 3
            ? ''
            : `${correctionTypeData[tile?.typeOfCorrection]?.short} bereinigt`
        }
        title={tile.name}
      />

      <Divider />

      <CardContent sx={{ p: '0 !important' }}>
        {isLoading && (
          <Collapse in={true}>
            <LinearProgress />
          </Collapse>
        )}

        {!isLoading && (
          <Collapse in={true}>
            <Collapse in={showWarning} sx={{ position: 'relative' }}>
              <Alert severity={'warning'} sx={{ width: 1, pr: 5, mb: 2 }}>
                {warningText}
              </Alert>

              <Tooltip title={'Messpunkte in Übersicht'}>
                <IconButton
                  onClick={onToggleWarning}
                  sx={{ position: 'absolute', top: 5, right: -5 }}
                >
                  <InfoTwoTone />
                </IconButton>
              </Tooltip>

              <Popover
                anchorEl={warningAnchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={() => setWarningAnchorEl(null)}
                open={Boolean(warningAnchorEl)}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack>
                  <Typography sx={{ fontWeight: 700, px: 2, py: 1 }}>
                    Messpunkte in Übersicht
                  </Typography>

                  <Divider />

                  <List sx={{ p: 1 }}>
                    {warningSensors.map(s => (
                      <ListItem key={String(s)}>
                        <ListItemText primary={s} />
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              </Popover>
            </Collapse>

            {chartType === 'PieChart' && noPieChartData ? (
              <Collapse in={noPieChartData}>
                <Alert
                  severity={'warning'}
                  sx={{
                    borderBottomLeftRadius: 20,
                    borderBottomRightRadius: 20,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderColor: theme.palette.lightgray.light,
                  }}
                >
                  Keine Daten zum Darstellen für diesen Zeitraum
                </Alert>
              </Collapse>
            ) : (
              <HStack sx={{ alignItems: 'stretch', display: 'flex', width: 1 }}>
                <style>
                  {`
                      .bar {
                      height: 8px;
                      border-radius: 4px;
                      margin-top: 4px;
                    }

                    .google-visualization-table-table {
                      border: 0 !important;
                    }

                    .google-visualization-table-type-number.google-visualization-table-td {
                      font-weight: bold;
                      text-align: center;
                    }

                    .google-visualization-table-tr,
                    .google-visualization-table-td,
                    .google-visualization-table-th {
                      border-left: none !important;
                      border-right: none !important;
                      border-bottom: 2px dotted #ccc !important;
                    }


                    .google-visualization-table-sortind {
                      color: ${theme.palette.primary.main};
                      display: inline !important;
                    }

                    .unsorted .google-visualization-table-sortind::after {
                      content: "▼";
                      display: inline !important;
                      color: #ccc;
                    }

                    .google-visualization-table-table tbody tr:last-child td,
                    .google-visualization-table-table tbody tr:last-child th {
                      border-bottom: none !important;
                    }

                    .google-visualization-table-th {
                      background-color: #fff;
                      border-bottom: 1px solid #ccc !important;
                      border-top: 1px solid #ccc !important;
                      font-size: 18px !important;
                    }
                    .google-visualization-table-th:first-child {
                      text-align: left !important;
                    }

                    .google-visualization-table-tr:last-child {
                      border-bottom: none !important;
                    }
      `}
                </style>
                <Stack
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Chart
                    chartLanguage={'de_DE'}
                    chartType={chartType}
                    data={chartData}
                    options={{
                      alternateRowStyle: false,
                      allowHtml: true,
                      chartArea,

                      // width: '100%' wirft TS Fehler, da Number erwartet wird und String geliefert wird
                      // vorerst mit ts-ignore umgehen, da Code aktuell funktioniert und Chart korrekterweise auf 100% skaliert
                      // weglassen setzt breite nicht auf 100%, anders als erwartet
                      // @ts-ignore
                      width: '100%',

                      height: panelHeight,
                      tooltip: { isHtml: true },
                      legend: 'none',
                      vAxis: {
                        format: `#,### ${chartUnit}`,
                        title: chartUnit ? `Wert (${chartUnit})` : 'Wert',
                        textStyle: { fontSize: 10 },
                        titleTextStyle: { fontSize: 12, italic: false },
                      },
                      hAxis: {
                        viewWindow: {
                          min:
                            selectedSensors.length === 0
                              ? new Date(tile.start_date)
                              : undefined,
                          max:
                            selectedSensors.length === 0
                              ? new Date(tile.end_date)
                              : undefined,
                        },
                        ticks: [
                          ...new Set(
                            chartData
                              .map((item, key) => {
                                if (!item?.[0] || key === 0) return null
                                const d =
                                  item[0] instanceof Date
                                    ? item[0]
                                    : new Date(item[0])
                                const fmt =
                                  tile.granularity === 'h'
                                    ? WDDDMMYYHHMM
                                    : tile.granularity === 'd'
                                      ? WDDDMMYYYY
                                      : tile.granularity === 'm'
                                        ? MMMMYYYY
                                        : tile.granularity === 'y'
                                          ? YYYY
                                          : DDMMYYYY
                                const label = isNaN(d.getTime())
                                  ? String(item[0])
                                  : d.toLocaleString(userLocales, fmt)
                                return { v: d, f: label }
                              })
                              .filter(f => f !== null)
                          ),
                        ] as any,
                        textStyle: { fontSize: 10 },
                      },
                      colors:
                        chartColors.length > 0
                          ? chartColors
                          : colorSchemeConsumption,
                      sliceVisibilityThreshold: 0,
                      isStacked: tile.isStacked,
                      pieStartAngle: 100,
                      pieSliceText: 'label',
                      pieSliceTextStyle: { fontSize: 12 },
                      sortColumn: 1,
                      sortAscending: false,
                      sort: 'enable',
                    }}
                  />

                  {(chartType === 'ColumnChart' ||
                    chartType === 'AreaChart' ||
                    chartType === 'LineChart') && (
                    <Stack
                      direction={'row'}
                      justifyContent={'flex-end'}
                      alignItems={'center'}
                    >
                      <ChartTypeSelector
                        onChange={handleGraphicType}
                        value={
                          tile.graphic_type === chartTypes.column_chart
                            ? 'ColumnChart'
                            : tile.graphic_type === chartTypes.line_chart
                              ? 'LineChart'
                              : tile.graphic_type === chartTypes.area_chart
                                ? 'AreaChart'
                                : 'PieChart'
                        }
                      />

                      {(chartType === 'ColumnChart' ||
                        chartType === 'AreaChart' ||
                        chartType === 'LineChart') && (
                        <StackSelector
                          onChange={handleStackMode}
                          value={tile.isStacked}
                        />
                      )}
                    </Stack>
                  )}
                </Stack>

                {(chartType === 'PieChart' ||
                  chartType === 'ColumnChart' ||
                  chartType === 'AreaChart' ||
                  chartType === 'LineChart') &&
                  tile.chartLegend === true &&
                  panelsPerRow !== 5 && (
                    <PanelDisplayNames
                      data={listSource}
                      colors={colorMap}
                      type={listLabel}
                      sx={{
                        minWidth: 0,
                        maxWidth: '20vw',
                        bgcolor: 'background.paper',
                        position: 'relative',
                        zIndex: 1,
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'stretch',
                        borderLeft: '1px solid #ccc',
                        p: 0,
                      }}
                    />
                  )}
              </HStack>
            )}
          </Collapse>
        )}
      </CardContent>

      <CardActions sx={{ borderTop: '1px solid #ccc' }}>
        {isLoading === true && (
          <Typography sx={{ justifyContent: 'center', width: 1 }}>
            wird aggregiert ...
          </Typography>
        )}

        <Collapse in={!isLoading} sx={{ width: 1 }}>
          {availableCorrectionTypes.length > 0 && (
            <HStack sx={{ width: 1, justifyContent: 'space-between' }}>
              <ToggleButtonGroup
                exclusive
                onChange={(_, newValue) => {
                  handleTypeOfCorrection(
                    newValue === null ? CORRECTION_ABSOLUT : Number(newValue)
                  )
                }}
                size={'small'}
                value={
                  tile.typeOfCorrection === CORRECTION_ABSOLUT
                    ? null
                    : (tile.typeOfCorrection ?? null)
                }
              >
                {availableCorrectionTypes
                  .filter(item => item.value !== CORRECTION_ABSOLUT)
                  .map(item => (
                    <Tooltip
                      key={item.short}
                      placement={'top'}
                      title={`Bereinigungsverfahren: ${item.label}`}
                    >
                      <ToggleButton value={item.value}>
                        {item.short}
                      </ToggleButton>
                    </Tooltip>
                  ))}
              </ToggleButtonGroup>

              <HStack>
                {'Summe'} &nbsp;
                <strong>{digitGrouping(total, tile?.typeOfCorrection)}</strong>
                &nbsp;
                {chartUnit}
              </HStack>
            </HStack>
          )}
        </Collapse>
      </CardActions>
    </Card>
  )
}
